/* Movatic WMS — Frontend | CesarFilho 932048016
   Arquivo: src/pages/workforce/Users.tsx
   -----------------------------------------------------------------------------
   “Gestão de Usuários” (quase paridade com o Django Admin)

   O que esta versão entrega:
   - Lista, busca, pagina, cria/edita, ativa/inativa e remove usuários.
   - Modal simples p/ criação/edição (com senha opcional em edição).
   - Carrega grupos e permissões (quando disponíveis) para atribuição.
   - Helper HTTP embutido com:
       • Bearer token automático
       • Normalização de Headers (corrige TS2769/HeadersInit)
       • Base da API por .env (VITE_API_BASE) com proteção contra "/api/api"
   - Fallbacks de endpoints: tenta `/auth/users/`, cai p/ `/users/` etc.
*/

import React, { useEffect, useMemo, useState } from 'react'
import {
  Edit3,
  Plus,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react'
import { listCompanies, type Company } from '@/lib/apiCore'

// =============================================================================
// Mini cliente HTTP com Bearer + base configurável
// =============================================================================

/** Lê VITE_API_BASE ou cai para '/api'. Ex.: '/api' OU 'http://127.0.0.1:8000/api' */
const API_BASE =
  ((import.meta.env as any)?.VITE_API_BASE as string | undefined)?.trim() ||
  '/api'

/** Junta base + path sem barras duplicadas. Evita '/api/api' e afins. */
function joinUrl(base: string, path: string) {
  const b = base.replace(/\/+$/, '')
  const p = path.replace(/^\/+/, '')
  return `${b}/${p}`
}

/** Header Authorization como Record<string,string> (evita TS2769) */
function authHeader(): Record<string, string> {
  const tok =
    localStorage.getItem('movatic_token') ||
    localStorage.getItem('access') ||
    localStorage.getItem('token')
  return tok ? { Authorization: `Bearer ${tok}` } : {}
}

/** Converte qualquer HeadersInit em Record<string,string> */
function normalizeHeaders(h?: HeadersInit): Record<string, string> {
  const out: Record<string, string> = {}
  if (!h) return out
  if (h instanceof Headers) {
    h.forEach((v, k) => (out[k] = String(v)))
  } else if (Array.isArray(h)) {
    h.forEach(([k, v]) => (out[k] = String(v)))
  } else {
    Object.assign(out, h as Record<string, string>)
  }
  return out
}

async function http<T>(url: string, init?: RequestInit): Promise<T> {
  const base: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  }
  const mergedHeaders: HeadersInit = {
    ...base,
    ...normalizeHeaders(init?.headers),
  }

  const finalUrl = joinUrl(API_BASE, url) // respeita VITE_API_BASE
  const res = await fetch(finalUrl, {
    ...init,
    headers: mergedHeaders,
    credentials: 'include', // compat cookie sessions
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || `${res.status} ${res.statusText}`)
  }
  if (res.status === 204) return undefined as unknown as T
  return (await res.json()) as T
}

/** GET com fallback de caminhos candidatos + querystring */
async function tryGet<T>(candidates: string[], params?: Record<string, any>) {
  let last: any
  const q = params
    ? '?' +
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(
          ([k, v]) =>
            `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
        )
        .join('&')
    : ''
  for (const base of candidates) {
    try {
      return { used: base, data: await http<T>(`${base}${q}`) }
    } catch (e) {
      last = e
    }
  }
  throw last
}
async function tryPost<T>(candidates: string[], body?: any) {
  let last: any
  for (const base of candidates) {
    try {
      return {
        used: base,
        data: await http<T>(base, {
          method: 'POST',
          body: JSON.stringify(body || {}),
        }),
      }
    } catch (e) {
      last = e
    }
  }
  throw last
}
async function tryPatch<T>(candidates: string[], body?: any) {
  let last: any
  for (const base of candidates) {
    try {
      return {
        used: base,
        data: await http<T>(base, {
          method: 'PATCH',
          body: JSON.stringify(body || {}),
        }),
      }
    } catch (e) {
      last = e
    }
  }
  throw last
}
async function tryDelete(candidates: string[]) {
  let last: any
  for (const base of candidates) {
    try {
      await http<void>(base, { method: 'DELETE' })
      return
    } catch (e) {
      last = e
    }
  }
  throw last
}

/** Endpoints candidatos (em ordem de preferência) */
const USERS_BASES = ['/users/users/'] // ADR-0036 F1: UserViewSet writable em /api/users/users/
const GROUPS_BASES = ['/auth/groups/', '/users/groups/', '/groups/']
const PERMS_BASES = [
  '/auth/permissions/',
  '/users/permissions/',
  '/permissions/',
]

// =============================================================================
// Tipagens
// =============================================================================

type UserDTO = {
  id?: number
  username: string
  email?: string
  first_name?: string
  last_name?: string
  company?: number | null
  role?: string | null
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
  groups?: number[] // ids de grupos
  user_permissions?: number[] // ids de permissões
}

type GroupDTO = { id: number; name: string }
type PermissionDTO = { id: number; name: string; codename?: string }
type Paged<T> = { count: number; results: T[] }

function unwrapList<T>(data: any): T[] {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.results)) return data.results
  return []
}
function unwrapCount(data: any): number {
  if (typeof data?.count === 'number') return data.count
  if (Array.isArray(data)) return data.length
  return 0
}

// =============================================================================
// Modal simples
// =============================================================================

function Modal(props: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  if (!props.open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-xl w-[min(880px,95vw)] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{props.title}</h3>
          <button
            className="iconbtn"
            onClick={props.onClose}
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto">{props.children}</div>
        {props.footer ? (
          <div className="mt-4 flex items-center justify-end gap-2">
            {props.footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// =============================================================================
// Página principal
// =============================================================================

export default function WorkforceUsers() {
  const [rows, setRows] = useState<UserDTO[]>([])
  const [count, setCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 20
  const pages = useMemo(
    () => Math.max(1, Math.ceil((count || 0) / pageSize)),
    [count],
  )

  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const emptyUser: UserDTO = {
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    company: null,
    role: 'operator',
    is_active: true,
    is_staff: false,
    is_superuser: false,
    groups: [],
    user_permissions: [],
  }
  const [model, setModel] = useState<UserDTO>(emptyUser)
  const [pwd1, setPwd1] = useState('')
  const [pwd2, setPwd2] = useState('')

  const [groups, setGroups] = useState<GroupDTO[] | null>(null)
  const [perms, setPerms] = useState<PermissionDTO[] | null>(null)
  const [companies, setCompanies] = useState<Company[]>([])

  function set<K extends keyof UserDTO>(k: K, v: UserDTO[K]) {
    setModel((m) => ({ ...m, [k]: v }))
  }

  async function loadCatalogs() {
    try {
      setCompanies(await listCompanies())
    } catch {
      setCompanies([])
    }
    try {
      const g = await tryGet<GroupDTO[] | Paged<GroupDTO>>(GROUPS_BASES)
      setGroups(unwrapList<GroupDTO>(g.data))
    } catch {
      setGroups(null)
    }
    try {
      const p = await tryGet<PermissionDTO[] | Paged<PermissionDTO>>(
        PERMS_BASES,
      )
      setPerms(unwrapList<PermissionDTO>(p.data))
    } catch {
      setPerms(null)
    }
  }

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const params = { search, page, page_size: pageSize, ordering: 'username' }
      const { data } = await tryGet<UserDTO[] | Paged<UserDTO>>(
        USERS_BASES,
        params,
      )
      setRows(unwrapList<UserDTO>(data))
      setCount(unwrapCount(data))
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar usuários.')
      setRows([])
      setCount(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    loadCatalogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((u) =>
      `${u.username} ${u.first_name} ${u.last_name} ${u.email}`
        .toLowerCase()
        .includes(q),
    )
  }, [rows, search])

  function openCreate() {
    setModel(emptyUser)
    setPwd1('')
    setPwd2('')
    setOpen(true)
  }
  function openEdit(u: UserDTO) {
    setModel({
      id: u.id,
      username: u.username,
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      email: u.email || '',
      is_active: !!u.is_active,
      is_staff: !!u.is_staff,
      is_superuser: !!u.is_superuser,
      groups: (u.groups || []) as number[],
      user_permissions: (u.user_permissions || []) as number[],
      company: (u.company as any) ?? null,
      role: (u.role as any) ?? null,
    })
    setPwd1('')
    setPwd2('')
    setOpen(true)
  }

  /** Aceita submit do form OU clique no botão do footer (evento opcional) */
  async function saveUser(ev?: React.FormEvent) {
    ev?.preventDefault()
    if (!model.username?.trim()) return alert('Informe o username.')
    if (!model.company)
      return alert(
        'Selecione a empresa (obrigatória — ADR-0036 D2, create e update).',
      )
    if (!model.id && !pwd1) return alert('Defina a senha do novo usuário.')
    if (pwd1 && pwd1 !== pwd2) return alert('As senhas não conferem.')

    setSaving(true)
    try {
      if (model.id) {
        // PATCH /users/{id}/
        const paths = USERS_BASES.map((b) => `${b}${model.id}/`)
        await tryPatch(paths, {
          username: model.username,
          first_name: model.first_name,
          last_name: model.last_name,
          email: model.email,
          is_active: model.is_active,
          is_staff: model.is_staff,
          is_superuser: model.is_superuser,
          groups: model.groups,
          user_permissions: model.user_permissions,
          company: model.company,
          role: model.role,
        })

        // Alterar senha (quando informada)
        if (pwd1) {
          try {
            const dedicated = USERS_BASES.map(
              (b) => `${b}${model.id}/set_password/`,
            )
            await tryPost(dedicated, { password: pwd1 })
          } catch {
            const pathsPwd = USERS_BASES.map((b) => `${b}${model.id}/`)
            await tryPatch(pathsPwd, { password: pwd1 })
          }
        }
      } else {
        // POST /users/
        await tryPost(USERS_BASES, { ...model, password: pwd1 })
      }

      setOpen(false)
      await load()
    } catch (e: any) {
      console.error(e)
      alert(e?.message || 'Falha ao salvar o usuário.')
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(u: UserDTO) {
    try {
      const paths = USERS_BASES.map((b) => `${b}${u.id}/`)
      await tryPatch(paths, { is_active: !u.is_active })
      await load()
    } catch {
      alert('Não foi possível alterar o status do usuário.')
    }
  }

  async function deleteUser(u: UserDTO) {
    if (
      !confirm(
        `Apagar usuário "${u.username}"? Esta ação não pode ser desfeita.`,
      )
    )
      return
    try {
      const paths = USERS_BASES.map((b) => `${b}${u.id}/`)
      await tryDelete(paths)
      await load()
    } catch {
      alert('Falha ao apagar o usuário.')
    }
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold">Workforce • Usuários</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Tela administrativa para cadastro e manutenção de usuários do
            sistema.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn" onClick={openCreate}>
            <Plus size={16} className="mr-2" /> Novo usuário
          </button>
          <button className="btn-secondary" onClick={load} title="Recarregar">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Busca */}
      <div className="mt-2 mb-3 flex gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) =>
            e.key === 'Enter' ? (setPage(1), load()) : undefined
          }
          className="input flex-1"
          placeholder="Buscar por username, nome ou e-mail…"
        />
        <button
          className="btn"
          onClick={() => {
            setPage(1)
            load()
          }}
        >
          Buscar
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setSearch('')
            setPage(1)
            load()
          }}
        >
          Limpar
        </button>
      </div>

      {/* Erro */}
      {error && (
        <div className="mb-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Tabela */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-neutral-200 dark:border-neutral-800">
              <th className="py-2 px-2 w-16">ID</th>
              <th className="py-2 px-2">Usuário</th>
              <th className="py-2 px-2">Nome</th>
              <th className="py-2 px-2">E-mail</th>
              <th className="py-2 px-2">Flags</th>
              <th className="py-2 px-2 text-right w-40">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-neutral-500">
                  Carregando…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-neutral-500">
                  Nenhum usuário encontrado.
                </td>
              </tr>
            ) : (
              filtered.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-neutral-200/60 dark:border-neutral-800/60"
                >
                  <td className="py-2 px-2">{u.id}</td>
                  <td className="py-2 px-2 font-mono">{u.username}</td>
                  <td className="py-2 px-2">
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') ||
                      '—'}
                  </td>
                  <td className="py-2 px-2">{u.email || '—'}</td>
                  <td className="py-2 px-2">
                    <span
                      className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}
                    >
                      {u.is_active ? 'ativo' : 'inativo'}
                    </span>{' '}
                    {u.is_staff ? (
                      <span className="badge badge-blue">staff</span>
                    ) : null}{' '}
                    {u.is_superuser ? (
                      <span className="badge badge-purple">superuser</span>
                    ) : null}
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        className="iconbtn"
                        title="Editar"
                        onClick={() => openEdit(u)}
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        className="iconbtn"
                        title={u.is_active ? 'Desativar' : 'Ativar'}
                        onClick={() => toggleActive(u)}
                      >
                        {u.is_active ? (
                          <ToggleRight size={18} />
                        ) : (
                          <ToggleLeft size={18} />
                        )}
                      </button>
                      <button
                        className="iconbtn text-red-500"
                        title="Apagar"
                        onClick={() => deleteUser(u)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Paginação */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-neutral-500">
            {count} itens • Página {page} de {pages}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <button
              className="btn-secondary"
              disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              Próxima
            </button>
          </div>
        </div>
      </div>

      {/* Modal de criar/editar */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={model?.id ? `Editar usuário: ${model.username}` : 'Novo usuário'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)}>
              Cancelar
            </button>
            {/* Botão do footer chama a mesma função do submit */}
            <button
              className="btn"
              onClick={() => saveUser()}
              disabled={saving}
            >
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <form className="grid md:grid-cols-2 gap-3" onSubmit={saveUser}>
          <div>
            <label className="block text-sm mb-1">Username</label>
            <input
              className="input"
              value={model.username}
              onChange={(e) => set('username', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">E-mail</label>
            <input
              className="input"
              value={model.email || ''}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Nome</label>
            <input
              className="input"
              value={model.first_name || ''}
              onChange={(e) => set('first_name', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Sobrenome</label>
            <input
              className="input"
              value={model.last_name || ''}
              onChange={(e) => set('last_name', e.target.value)}
            />
          </div>

          {/* ADR-0036 D2: empresa obrigatoria (fonte unica); perfil (role) */}
          <div>
            <label className="block text-sm mb-1">Empresa *</label>
            <select
              className="input"
              value={model.company ?? ''}
              onChange={(e) =>
                set('company', e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">— selecionar —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Perfil</label>
            <select
              className="input"
              value={model.role ?? 'operator'}
              onChange={(e) => set('role', e.target.value)}
            >
              <option value="admin">admin</option>
              <option value="manager">manager</option>
              <option value="operator">operator</option>
            </select>
          </div>

          {/* Senha (opcional em edição) */}
          <div>
            <label className="block text-sm mb-1">
              Senha{' '}
              {model.id ? (
                <span className="opacity-60">
                  (deixe em branco para não alterar)
                </span>
              ) : null}
            </label>
            <input
              type="password"
              className="input"
              value={pwd1}
              onChange={(e) => setPwd1(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Confirmar senha</label>
            <input
              type="password"
              className="input"
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
            />
          </div>

          {/* Flags */}
          <div className="col-span-full grid grid-cols-3 gap-3">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!model.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              <span>Ativo</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!model.is_staff}
                onChange={(e) => set('is_staff', e.target.checked)}
              />
              <span>Staff</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!model.is_superuser}
                onChange={(e) => set('is_superuser', e.target.checked)}
              />
              <span>Superuser</span>
            </label>
          </div>

          {/* Catálogos opcionais: grupos/permissões */}
          {groups && groups.length > 0 && (
            <div>
              <label className="block text-sm mb-1">Grupos</label>
              <select
                multiple
                className="input min-h-[120px]"
                value={(model.groups || []) as any}
                onChange={(e) => {
                  const vals = Array.from(e.target.selectedOptions).map((o) =>
                    Number(o.value),
                  )
                  set('groups', vals as any)
                }}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {perms && perms.length > 0 && (
            <div>
              <label className="block text-sm mb-1">Permissões</label>
              <select
                multiple
                className="input min-h-[120px]"
                value={(model.user_permissions || []) as any}
                onChange={(e) => {
                  const vals = Array.from(e.target.selectedOptions).map((o) =>
                    Number(o.value),
                  )
                  set('user_permissions', vals as any)
                }}
              >
                {perms.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || p.codename || p.id}
                  </option>
                ))}
              </select>
            </div>
          )}
        </form>
      </Modal>
    </div>
  )
}
